import type { InvoiceDetail } from '../useQueryInvoiceDetail/types';
import type { QueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';
import { zodInvoiceDetailSchema } from '../useQueryInvoiceDetail/validations';

/**
 * Caches the invoice a link mutation answered with, dropping a detail fetch
 * still in flight that would overwrite it with older links. A body that fails
 * the schema is not cached; the detail is refetched instead.
 *
 * @param queryClient - The query client holding the detail cache.
 * @param invoiceId - Invoice whose detail entry is written.
 * @param invoice - The response body to validate and cache.
 * @returns A promise that settles once the cache is written or refetched.
 */
export async function writeInvoiceDetail(
  queryClient: QueryClient,
  invoiceId: UUID,
  invoice: unknown,
) {
  const queryKey = queryKeysInvoice.getDetail(invoiceId);
  const parsedInvoice = zodInvoiceDetailSchema.safeParse(invoice);

  if (!parsedInvoice.success) {
    await queryClient.invalidateQueries({ queryKey });
    return;
  }

  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData<InvoiceDetail>(queryKey, parsedInvoice.data);
}
